import { defineStore, storeToRefs } from "pinia"
import { computed, ref } from "vue"
import { useLoginStore } from "@/stores/login"
import { useSessionIdStore } from "@/stores/sessionId"
import { axiosRetryx } from '@/utils/axios_retryx'

export type KuraTelJoinRequest = {
  /** 呼び出されているkuraTelRoomId */
  kuraTelRoomId: string,

  /** 蔵電roomの作成者  */
  startAccount: {
    accountId: string,
    display: null | {
      displayName: string,
      iconUrl: string,
    }
  },

  /** ユーザー画面上に表示するメッセージ */
  message: string,
}

export const TokenError = {
  /** token_expired: トークンの有効期限切れ → 再ログインで解決 */
  Expired: class TokenExpiredError extends Error {
    constructor() {
      super('Access token has expired')
      this.name = 'TokenExpiredError'
    }
  },
  /** invalid_token: トークンが無効（旧世代のトークン等） → 再ログイン不要、無視してよい */
  Invalid: class TokenInvalidError extends Error {
    constructor() {
      super('Invalid access token')
      this.name = 'TokenInvalidError'
    }
  },
  throw(error: unknown): never | void {
    const errorCode = (error as any)?.response?.data?.error
    if (errorCode === 'token_expired') throw new this.Expired()
    if (errorCode === 'invalid_token') throw new this.Invalid()
  },
}

type LoginRequest = (
  | {
    /** 開発中のみ利用可能  */
    forDevelop: {
      accountId: string,
    }
  }
  | {
    webBrowser: {
      sessionId: string,
      accountId: string,
    }
  }
)
type LoginResponse = {
  liveStatusToken: string,
  webSocketUrl: string,
}
type CallMemberListQueryType = (
  | { constructionIds: string[] }  // 特定の工事のアカウントのリストから取得する
)
type StatusQueryRequest = {
  /** 参加アカウント要約状態を応答するconstructionId群。 */
  accountListDigestConstructionIds: Array<string>,
  /** 電話ダイアログに表示するリストを取得するアクセス種別 */
  callMemberListQueryType?: (
    | CallMemberListQueryType
    | null
  ),
  /** 参加者状態を応答する蔵衛門電話ルーム  */
  kuraTelRoom?: (
    | null
    | {
      kuraTelRoomId: string,
      /** ==trueで、displayName, iconUrl も応答する。 */
      withAccountDisplayInfo: boolean,
    }
  ),
} 
type StatusQueryResponse = {
  liveStatusToken: string,
} 
type HeartBeatRequest = {
  queryResultEtag: null | string,
}
type HeartBeatResponse = {
  /**
   * !==null : 認証トークン更新が必要な場合
   * ===null : 認証トークン更新の不必要な場合
   */
  liveStatusToken: null | string,

  queryResultEtag: string,
  queryResult: (
    /** request.queryResultEtag == response.queryResultEtag の場合。==状態から変化がない場合。  */
    | null
    | {
      accountLiveStatuses: Array<{
        accountId: string,
        isOnLine: boolean,
        isOnTel: boolean,
      }>,
      accountListDigestConstructions: Array<{
        constructionId: string,
        digestJoinAccountIdList: Array<string>,
        joinAccountCount: number,
        patrolCameraCount: number,
        existOnlinePatrolCamera: boolean,
      }>,
      callMemberList: (
        | {
          callMemberListQueryType : CallMemberListQueryType,
          accountIdList: Array<string>,
        }
        | null
      ),

      /** 蔵衛門電話ルームの状態  */
      kuraTelRoom: (
        | null
        | {
          kuraTelRoomId: string,

          /** 通話開始日時。 */
          startAt: number,
          /** !==nullは通話終了日時。===nullは通話中。  */
          finishedAt: null | number,

          /** 参加者  */
          accountInfos: Array<{
            accountId: string,
            /** chimeSDK上のattendeeと対応させるための externalUserId */
            chimeExternalUserId: null | string,
            /**
             * 参加者状態。
             *  chimeExternalUerId!==nullで、chimeSDKのattendeeと対応する場合は、chimeSDKが返す状態を表示する。
             *  それ以外の、chimeSDKのattendeeから状態を確認できない場合に conditions の状態で表示する。
             */
            conditions: {
              /** 呼び出し中。 */
              isCalling: boolean,
              /** 通話終了操作または受電拒否操作をした。 */
              isClosed: boolean,
            },

            /** withAccountDisplayInfo===trueの場合のみ。  */
            display: null | {
              displayName: string,
              iconUrl: string,
            }
          }>,
        }
      ),

      /** 蔵電roomへの参加要求。  */
      kuraTelJoinRequest: KuraTelJoinRequest | null,
    }
  ),
}
type KuraemonTelStatus= {
  kuraTelRoomId: string,
  status: (
    | {
      /** ウェブブラウザクライアントで参加操作をして別タブで蔵門電話トークビューページを開いた。  */
      openKuraTelViewPage: {}
    }
    | { accept: { // 参加した
      /** chime sdkから取得した最新のroomの状況。 */
      attendeeStatuses: Array<{
        attendeeId: string,
        externalUserId: string,
        attendeeStatus: 'joined' | 'left' | 'dropped',
        lastSentAt: number,
      }>,
    } }
    | { reject: {} } // 拒否した
    | { leave: {} } // 退出した
  )
}
type ClientStatusQuerySourceRequest = {
  /** 蔵衛門電話のmeetingの参加状態 */
  kuraemonTelStatus: (
    | KuraemonTelStatus
    | null
  )
}

type MakeKuraTelRoomRequest = {
  /** 呼び出す先のアカウントID群  */
  callAccountIds: Array<string>,
  /** ルーム作成時点で呼び出しを発火するか（false の場合、readyRoom で発火） */
  roomReady?: boolean,
}
type MakeKuraTelRoomResponse = {
  /** 作成されたトークルームID */
  kuraTelRoomId: string,
}

const API_BASE_URL = import.meta.env.VITE_APP_KURADEN_API_BASE_URL;

/**
 * API呼び出しを直列化するキュー。
 * サーバーは __liveStatusToken (JWT) で呼び出し順序の整合性をチェックするため、
 * すべてのAPI呼び出しは直列で実行する必要がある。
 */
let __apiQueue: Promise<unknown> = Promise.resolve()
const serialized = <T>(fn: () => Promise<T>): Promise<T> => {
  const task = __apiQueue.then(fn, fn)
  __apiQueue = task.then(() => {}, () => {})
  return task
}

export const useKuradenApiStore = defineStore('kuradenApi', () => {
  const { currentAccount } = storeToRefs(useLoginStore())
  const { getSessionId } = useSessionIdStore()

  const __liveStatusToken = ref<string | null>(null)
  const liveStatusToken = computed(() => __liveStatusToken.value)
  const __queryResultEtag = ref<string | null>(null)
  const clearSession = () => {
    __liveStatusToken.value = null
    __queryResultEtag.value = null
  }

  /**
   * ログインAPI
   * 必要認証トークン : (なし)
   */
  const login = () => serialized(async () => {
    const requestBody: LoginRequest = {
      webBrowser: {
        sessionId: getSessionId(),
        accountId: currentAccount.value.account_id,
      }
    }

    try {
      const response = await axiosRetryx({
        method: 'post',
        url: `${API_BASE_URL}/login`,
        headers:{ 'Content-Type': 'application/json;charset=utf-8' },
        data: requestBody
      }, { maxTries: 3, timeout: 90000 });

      const loginResponse = response.data as LoginResponse;
      __liveStatusToken.value = loginResponse.liveStatusToken;
      return loginResponse;
    } catch (error) {
      console.error('Error calling API:', error);
    }
  })

  /**
   * 状態クエリ指定
   * 必要認証トークン : liveStatusToken
   */
  const statusQuery = (accountListDigestConstructionIds: Array<string>, callMemberListQueryType: CallMemberListQueryType | null) => serialized(async () => {
    const requestBody: StatusQueryRequest = {
      accountListDigestConstructionIds,
      callMemberListQueryType,
    };
    try {
      const response = await axiosRetryx({
        method: 'post',
        url: `${API_BASE_URL}/statusQuery`,
        headers:{ 'Content-Type': 'application/json;charset=utf-8', Authorization: `Bearer ${__liveStatusToken.value}` },
        data: requestBody
      });

      const result = response.data as StatusQueryResponse;
      __liveStatusToken.value = result.liveStatusToken;
      __queryResultEtag.value = null;
      return result;
    } catch (error) {
      console.error('Error calling API:', error);
      return null
    }
  })

  /**
   * 状態取得 + クライアントの生存報告。
   * 必要認証トークン : liveStatusToken
   */
  const heartBeat = () => serialized(async () => {
    const requestBody: HeartBeatRequest = {
      queryResultEtag: __queryResultEtag.value,
    };
    try {
      const response = await axiosRetryx({
        method: 'post',
        url: `${API_BASE_URL}/heartBeat`,
        headers:{ 'Content-Type': 'application/json;charset=utf-8', Authorization: `Bearer ${__liveStatusToken.value}` },
        data: requestBody
      });

      const heartBeatResponse = response.data as HeartBeatResponse;
      if (heartBeatResponse.liveStatusToken) {
        __liveStatusToken.value = heartBeatResponse.liveStatusToken;
      }
      __queryResultEtag.value = heartBeatResponse.queryResultEtag;

      return heartBeatResponse;
    } catch (error) {
      // token_expired/invalid_token の場合は呼び出し元で判断するためre-throw
      TokenError.throw(error)
      console.error('Error calling API:', error);
    }
  })

  /**
   * サーバー側でqueryResultを構築するために必要な各clientの状態の更新を通知する
   * 必要認証トークン : liveStatusToken
   */
  const clientStatusQuerySource = (kuraemonTelStatus: KuraemonTelStatus) => serialized(async () => {
    const requestBody: ClientStatusQuerySourceRequest = {
      kuraemonTelStatus,
    }

    try {
      await axiosRetryx({
        method: 'post',
        url: `${API_BASE_URL}/clientStatusQuerySource`,
        headers:{ 'Content-Type': 'application/json;charset=utf-8', Authorization: `Bearer ${__liveStatusToken.value}` },
        data: requestBody
      });
    } catch (error) {
      console.error('Error calling API:', error);
    }
  })

  /**
   * 蔵衛門電話ルーム作成API
   * 必要認証トークン : liveStatusToken
   */
  const makeKuraTelRoom = (callAccountIds: Array<string>) => serialized(async () => {
    const requestBody: MakeKuraTelRoomRequest = {
      callAccountIds,
      roomReady: false,
    }
    try {
      const response = await axiosRetryx({
        method: 'post',
        url: `${API_BASE_URL}/kuraTelRoom`,
        headers:{ 'Content-Type': 'application/json;charset=utf-8', Authorization: `Bearer ${__liveStatusToken.value}` },
        data: requestBody
      });
      return response.data as MakeKuraTelRoomResponse;
    } catch (error) {
      console.error('Error calling API:', error);
    }
  })

  return {
    __liveStatusToken,
    __queryResultEtag,
    liveStatusToken,
    clearSession,
    login,
    statusQuery,
    heartBeat,
    clientStatusQuerySource,
    makeKuraTelRoom,
  }
})
